import { utmParams } from '@services/utms';

const FIRST_TOUCH_COOKIE = 'pb_first_touch';
const LAST_TOUCH_COOKIE = 'pb_last_touch';
const COOKIE_MAX_AGE = 90 * 24 * 60 * 60;

const FT_PREFIX = 'ft_';
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

type CookieData = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  creator_slug?: string;
  referrer_url?: string;
  raw_landing_url?: string;
};

type AttributionPayload = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrerUrl?: string;
  firstTouchUtmSource?: string;
  firstTouchUtmMedium?: string;
  firstTouchUtmCampaign?: string;
  firstTouchUtmTerm?: string;
  firstTouchUtmContent?: string;
  firstTouchReferrerUrl?: string;
  rawLandingUrl?: string;
  firstTouchRawLandingUrl?: string;
  creatorSlug?: string;
  firstTouchCreatorSlug?: string;
  signupPlatform: 'web';
};

function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';');

  for (const cookieEntry of cookies) {
    const trimmedEntry = cookieEntry.trim();
    const separatorIndex = trimmedEntry.indexOf('=');
    const cookieName =
      separatorIndex === -1
        ? trimmedEntry
        : trimmedEntry.slice(0, separatorIndex);

    if (cookieName !== name) {
      continue;
    }

    const cookieValue =
      separatorIndex === -1 ? '' : trimmedEntry.slice(separatorIndex + 1);

    return decodeURIComponent(cookieValue);
  }

  return null;
}

function parseCookie(name: string): CookieData | null {
  try {
    const raw = getCookie(name);

    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as CookieData;
  } catch {
    return null;
  }
}

function mapLastTouch(data: CookieData): Partial<AttributionPayload> {
  const result: Partial<AttributionPayload> = {};

  if (data.utm_source) result.utmSource = data.utm_source;
  if (data.utm_medium) result.utmMedium = data.utm_medium;
  if (data.utm_campaign) result.utmCampaign = data.utm_campaign;
  if (data.utm_term) result.utmTerm = data.utm_term;
  if (data.utm_content) result.utmContent = data.utm_content;
  if (data.referrer_url) result.referrerUrl = data.referrer_url;
  if (data.raw_landing_url) result.rawLandingUrl = data.raw_landing_url;
  if (data.creator_slug) result.creatorSlug = data.creator_slug;

  return result;
}

function mapFirstTouch(data: CookieData): Partial<AttributionPayload> {
  const result: Partial<AttributionPayload> = {};

  if (data.utm_source) result.firstTouchUtmSource = data.utm_source;
  if (data.utm_medium) result.firstTouchUtmMedium = data.utm_medium;
  if (data.utm_campaign) result.firstTouchUtmCampaign = data.utm_campaign;
  if (data.utm_term) result.firstTouchUtmTerm = data.utm_term;
  if (data.utm_content) result.firstTouchUtmContent = data.utm_content;
  if (data.referrer_url) result.firstTouchReferrerUrl = data.referrer_url;
  if (data.raw_landing_url)
    result.firstTouchRawLandingUrl = data.raw_landing_url;
  if (data.creator_slug) result.firstTouchCreatorSlug = data.creator_slug;

  return result;
}

function mapLocalStorageToLastTouch(): Partial<AttributionPayload> {
  const stored = utmParams.restore();
  const result: Partial<AttributionPayload> = {};

  if (stored.utm_source) result.utmSource = stored.utm_source;
  if (stored.utm_medium) result.utmMedium = stored.utm_medium;
  if (stored.utm_campaign) result.utmCampaign = stored.utm_campaign;
  if (stored.utm_term) result.utmTerm = stored.utm_term;
  if (stored.utm_content) result.utmContent = stored.utm_content;

  return result;
}

function mapLocalStorageToFirstTouch(): Partial<AttributionPayload> {
  const stored = utmParams.restore();
  const result: Partial<AttributionPayload> = {};

  if (stored.utm_source) result.firstTouchUtmSource = stored.utm_source;
  if (stored.utm_medium) result.firstTouchUtmMedium = stored.utm_medium;
  if (stored.utm_campaign) result.firstTouchUtmCampaign = stored.utm_campaign;
  if (stored.utm_term) result.firstTouchUtmTerm = stored.utm_term;
  if (stored.utm_content) result.firstTouchUtmContent = stored.utm_content;

  return result;
}

function hasFields(obj: Partial<AttributionPayload>): boolean {
  return Object.keys(obj).length > 0;
}

function setCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
}

function extractFtParams(search: string): CookieData {
  const params = new URLSearchParams(search);
  const data: CookieData = {};

  for (const key of UTM_KEYS) {
    const value = params.get(`${FT_PREFIX}${key}`);

    if (value) {
      data[key] = value;
    }
  }

  return data;
}

function hasCookieData(data: CookieData): boolean {
  return UTM_KEYS.some((key) => Boolean(data[key]));
}

export function captureAttribution(): void {
  const utms = utmParams.getFromURL(window.location.search);
  const ftParams = extractFtParams(window.location.search);
  const hasUtms = Object.keys(utms).length > 0;
  const hasFt = hasCookieData(ftParams);

  if (!hasUtms && !hasFt) {
    return;
  }

  if (hasUtms) {
    const lastTouchData: CookieData = {
      ...utms,
      referrer_url: document.referrer || undefined,
      raw_landing_url: window.location.href,
    };

    setCookie(LAST_TOUCH_COOKIE, JSON.stringify(lastTouchData));
  }

  const existingFirstTouch = getCookie(FIRST_TOUCH_COOKIE);

  if (!existingFirstTouch) {
    const firstTouchData: CookieData = hasFt
      ? {
          ...ftParams,
          referrer_url: document.referrer || undefined,
          raw_landing_url: window.location.href,
        }
      : {
          ...utms,
          referrer_url: document.referrer || undefined,
          raw_landing_url: window.location.href,
        };

    setCookie(FIRST_TOUCH_COOKIE, JSON.stringify(firstTouchData));
  }
}

export function getAttributionPayload(): AttributionPayload {
  const lastTouchCookie = parseCookie(LAST_TOUCH_COOKIE);
  const firstTouchCookie = parseCookie(FIRST_TOUCH_COOKIE);

  const lastTouch = lastTouchCookie
    ? mapLastTouch(lastTouchCookie)
    : mapLocalStorageToLastTouch();

  const firstTouch = firstTouchCookie
    ? mapFirstTouch(firstTouchCookie)
    : hasFields(lastTouch)
    ? mapLocalStorageToFirstTouch()
    : {};

  return {
    ...lastTouch,
    ...firstTouch,
    signupPlatform: 'web',
  };
}

export const __tests__ = {
  getCookie,
  setCookie,
  parseCookie,
  mapLastTouch,
  mapFirstTouch,
  extractFtParams,
  hasCookieData,
  FIRST_TOUCH_COOKIE,
  LAST_TOUCH_COOKIE,
};
